import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import styles from "./Home.module.scss";

// --- Inlined Button Component ---
const Button = ({ text, onClick, className = "" }) => {
  const isBorderBtn = className.includes("border_btn");
  return (
    <button
      onClick={onClick}
      className={`page-btn ${isBorderBtn ? "page-btn-border border_btn" : "page-btn-primary"} ${className}`}
    >
      {text}
    </button>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userType = localStorage.getItem("userType");

    if (token) {
      if (userType === "Internal") {
        navigate("/admin/dashboard");
      } else {
        navigate("/dashboard");
      }
    }
  }, [navigate]);

  const goSandbox = () => {
    const token = localStorage.getItem("authToken");
    const userType = localStorage.getItem("userType");
    if (token) {
      if (userType === "Internal") {
        navigate("/admin/admin-catalogue");
      } else {
        navigate("/api-catalogue");
      }
    } else {
      navigate("/api-catalogue-home");
    }
  };

  const childEase = {
    duration: reduceMotion ? 0 : 0.6,
    ease: [0.22, 1, 0.36, 1],
  };

  const containerVariants = {
    hidden: { opacity: reduceMotion ? 1 : 0 },
    show: {
      opacity: 1,
      transition: reduceMotion
        ? { duration: 0 }
        : { staggerChildren: 0.1, delayChildren: 0.1 },
    },
  };

  const itemFade = {
    hidden: { opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: childEase,
    },
  };

  const floatOrbA = reduceMotion
    ? { opacity: 0.15, y: 0, scale: 1 }
    : {
        opacity: [0.15, 0.3, 0.15],
        y: [0, -20, 0],
        x: [0, 10, 0],
        scale: [1, 1.1, 1],
      };

  const floatOrbB = reduceMotion
    ? { opacity: 0.15, y: 0, scale: 1 }
    : {
        opacity: [0.1, 0.25, 0.1],
        y: [0, 25, 0],
        x: [0, -10, 0],
        scale: [1, 1.05, 1],
      };

  return (
    <>
      <div className={styles["page-wrapper"]}>
        <motion.section
          className={styles["page-hero"]}
          id="ai-hero"
          aria-label="Doql"
          initial={false}
        >
          {/* Background Effects & Orbs */}
          <div className={styles["page-bg-fx"]} aria-hidden>
            <div className={styles["page-bg-grid"]} />
            <motion.div
              className={styles["page-orb-a"]}
              initial={false}
              animate={floatOrbA}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { duration: 18, repeat: Infinity, ease: "easeInOut" }
              }
            />
            <motion.div
              className={styles["page-orb-b"]}
              initial={false}
              animate={floatOrbB}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { duration: 15, repeat: Infinity, ease: "easeInOut" }
              }
            />
            <div className={styles["page-bg-gradient"]} />
          </div>

          {/* Main Content Container */}
          <div className="sec-container">
            <div className={styles["page-grid"]}>
              {/* Left Column: Text & CTAs */}
              <div className={styles["page-col-left"]}>
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                  className={styles["page-content-max"]}
                >
                  <motion.span className="page-eyebrow" variants={itemFade}>
                    For Banks, NBFCs, Fintechs, Insurers &amp; Regulated
                    Enterprises
                  </motion.span>

                  <motion.h1 className={styles["page-h1"]} variants={itemFade}>
                    API, SaaS, and PaaS for regulated industries—starting with{" "}
                    <span className="page-text-gradient">BFSI in India.</span>
                  </motion.h1>

                  <motion.p className="page-lead" variants={itemFade}>
                    One platform for identity, financial data, fraud,
                    compliance, and document verification—deliver as APIs, SaaS,
                    or on your stack (PaaS). ISO 27001 and ISO 9001 certified;
                    aligned to SOC 2 Type II, PCI-DSS Level 1, and DPDPA.
                  </motion.p>

                  <motion.div className={styles["page-btn-group"]} variants={itemFade}>
                    <Button
                      text="Book a demo"
                      onClick={() => navigate("/contact")}
                    />
                    {/* <Button
                      text="Start in sandbox"
                      className="border_btn"
                      onClick={goSandbox}
                    /> */}
                  </motion.div>

                  <motion.div
                    className={styles["page-secondary-links"]}
                    variants={itemFade}
                  >
                    <button
                      type="button"
                      className={styles["page-link-btn"]}
                      onClick={() => navigate("/contact?topic=compliance-pack")}
                    >
                      Download compliance pack (ISO 27001, ISO 9001, SOC 2,
                      PCI-DSS)
                    </button>

                    <div
                      className={styles["page-trust-strip"]}
                      aria-label="Service and compliance highlights"
                    >
                      <span>99.9% uptime</span>
                      <span className={styles["page-trust-dot"]}></span>
                      <span>&lt;100ms response</span>
                      <span className={styles["page-trust-dot"]}></span>
                      <span>ISO 27001</span>
                      <span className={styles["page-trust-dot"]}></span>
                      <span>ISO 9001</span>
                      <span className={styles["page-trust-dot"]}></span>
                      <span>SOC 2 Type II</span>
                      <span className={styles["page-trust-dot"]}></span>
                      <span>PCI-DSS Level 1</span>
                      <span className={styles["page-trust-dot"]}></span>
                      <span>DPDPA-ready</span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              {/* Right Column: Dashboard UI */}
              <div className={styles["page-col-right"]}>
                <motion.div
                  initial={{ opacity: 0, y: 40, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                  style={{ perspective: "1000px" }}
                >
                  <div className={styles["page-dashboard"]} aria-label="Product preview">
                    {/* Dashboard Header / Chrome */}
                    <div className={styles["page-dash-header"]}>
                      <div className={styles["page-dash-dots"]}>
                        <span className={`${styles["page-dot"]} ${styles["red"]}`}></span>
                        <span className={`${styles["page-dot"]} ${styles["yellow"]}`}></span>
                        <span className={`${styles["page-dot"]} ${styles["green"]}`}></span>
                      </div>
                      <p className={styles["page-dash-title-sm"]}>API.Endpoint</p>
                    </div>

                    {/* Dashboard Content */}
                    <div className={styles["page-dash-body"]}>
                      <div className={styles["page-dash-row"]}>
                        <p className={styles["page-dash-title"]}>Verify Identity</p>
                        <span className={styles["page-ping-wrapper"]}>
                          <span className={styles["page-ping-anim"]}></span>
                          <span className={styles["page-ping-dot"]}></span>
                        </span>
                      </div>

                      <div className={styles["page-code-block"]}>
                        <pre className={styles["page-pre"]} tabIndex={0}>
                          <span className={styles["page-code-bracket"]}>{"{"}</span>
                          <br />
                          <span className={styles["page-code-key"]}>"status"</span>
                          <span className={styles["page-code-colon"]}>: </span>
                          <span className={styles["page-code-str"]}>"ok"</span>
                          <span className={styles["page-code-colon"]}>,</span>
                          <br />
                          <span className={styles["page-code-key"]}>"verified"</span>
                          <span className={styles["page-code-colon"]}>: </span>
                          <span className={styles["page-code-bool"]}>true</span>
                          <span className={styles["page-code-colon"]}>,</span>
                          <br />
                          <span className={styles["page-code-key"]}>"name_match"</span>
                          <span className={styles["page-code-colon"]}>: </span>
                          <span className={styles["page-code-num"]}>0.98</span>
                          <br />
                          <span className={styles["page-code-bracket"]}>{"}"}</span>
                        </pre>
                      </div>

                      {/* Dashboard Footer Badges */}
                      <div className={styles["page-badges"]}>
                        <span className={styles["page-badge"]}>
                          <svg
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                          </svg>
                          ISO 27001
                        </span>
                        <span className={styles["page-badge"]}>
                          <svg
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                          </svg>
                          SOC 2
                        </span>
                        <span className={styles["page-badge"]}>
                          <svg
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                          </svg>
                          PCI-DSS
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>
      </div>
    </>
  );
}
